// src/pages/CleanersPage.js
import React, { useState, useEffect } from 'react'
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions } from '../firebaseConfig'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'

// 🔹 DAFTAR LAYANAN
const SERVICE_MASTER = [
  { code: 'RAT', label: 'Rumah Alat Mitra' },
  { code: 'RAP', label: 'Rumah Alat Pribadi' },
  { code: 'AAM', label: 'Apartemen Alat Mitra' },
  { code: 'AAP', label: 'Apartemen Alat Pribadi' },
  { code: 'KAM', label: 'Kost Alat Mitra' },
  { code: 'SAP', label: 'Setrika Alat Pribadi' },
]

export default function CleanersPage() {
  const [cleaners, setCleaners] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  
  // Form States
  const [showForm, setShowForm] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editId, setEditId] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  
  // Input Kota
  const [cityInput, setCityInput] = useState('')

  // Detail Panel
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedCleaner, setSelectedCleaner] = useState(null)

  const initialFormState = {
    name: '',
    email: '',
    phone: '',
    id_number: '',
    gender: 'wanita', 
    photoUrl: '',
    rating: 5.0,
    saldo: 0,
    status: true,
    isBookingActive: true,
    seragam: false, // 🟢 FIELD BARU: Default False
    cities: [],      
    services: [],
    referralCodeInput: '',
    target_area_override: '' // 👈 TAMBAHAN: Field Kunci Area Manual
  }

  const [formData, setFormData] = useState(initialFormState)

  // 👈 TAMBAHAN: Helper untuk format jam terakhir dilihat
  const formatLastSeen = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  // 👈 FUNGSI PEMBANTU: Memproses data mentah cleaners
  const processCleanerData = (snap) => {
    const data = snap.docs.map(d => ({
      id: d.id, 
      ...d.data(),
      services: d.data().services || [],
      cities: d.data().cities || [],
      rating: d.data().rating || 0,
      saldo: d.data().saldo || 0,
      referralCode: d.data().referralCode || '-',
      seragam: d.data().seragam || false,
      is_app_open: d.data().is_app_open || false,
      last_app_opened: d.data().last_app_opened || null,
      target_area_override: d.data().target_area_override || '' // 👈 TAMBAHAN
    }));
    // Urutkan berdasarkan waktu buat
    return data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  };

  // 👈 FUNGSI FETCH MANUAL (Tetap ada agar tombol Simpan/Hapus tidak error)
  const fetchCleaners = async () => {
    const snap = await getDocs(collection(db, 'cleaners'));
    const sortedData = processCleanerData(snap);
    setCleaners(sortedData);
    setFiltered(sortedData);
  };

  // 👈 LISTENER REAL-TIME: Status "Sedang Buka App" akan otomatis berubah
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'cleaners'), (snap) => {
      const sortedData = processCleanerData(snap);
      setCleaners(sortedData);
      setFiltered(sortedData);
      setLoading(false);
    }, (err) => {
      console.error("Real-time error Cleaners:", err);
      setLoading(false);
    });

    return () => unsub(); // Hapus listener saat halaman ditutup
  }, []);

  // 🔹 2. SEARCH
  useEffect(() => {
    const s = search.toLowerCase()
    const result = cleaners.filter(c => {
      const servicesStr = c.services.map(srv => srv.serviceId).join(' ').toLowerCase()
      const citiesStr = c.cities.join(' ').toLowerCase()
      return (
        c.name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.id_number?.toLowerCase().includes(s) ||
        servicesStr.includes(s) ||
        citiesStr.includes(s)
      )
    })
    setFiltered(result)
  }, [search, cleaners])

  // 🔹 3. UPLOAD FOTO
  const handleUploadPhoto = (file) => {
    if (!file) return
    const fileName = `cleaners_photos/${Date.now()}_${file.name}`
    const storageRef = ref(storage, fileName)
    const uploadTask = uploadBytesResumable(storageRef, file)

    setUploading(true)
    uploadTask.on('state_changed', null, 
      (err) => {
        setUploading(false)
        alert('Upload Gagal: ' + err.message)
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref)
        setFormData(prev => ({ ...prev, photoUrl: url }))
        setPreviewUrl(url)
        setUploading(false)
      }
    )
  }

  // 🔹 4. KOTA & SERVICES
  const addCity = (e) => {
    e.preventDefault()
    const val = cityInput.trim().toLowerCase()
    if (val && !formData.cities.includes(val)) {
      setFormData(prev => ({ ...prev, cities: [...prev.cities, val] }))
    }
    setCityInput('')
  }

  const removeCity = (cityToRemove) => {
    setFormData(prev => ({
      ...prev,
      cities: prev.cities.filter(c => c !== cityToRemove)
    }))
  }

  const toggleService = (code, checked) => {
    setFormData(prev => {
      let currentServices = [...prev.services]
      if (checked) {
        if (!currentServices.some(s => s.serviceId === code)) {
          currentServices.push({ serviceId: code })
        }
      } else {
        currentServices = currentServices.filter(s => s.serviceId !== code)
      }
      return { ...prev, services: currentServices }
    })
  }

  // 🔹 5. SIMPAN DATA
  const handleSave = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.email || !formData.phone) 
      return alert("Nama, Email, dan No HP wajib diisi.")

    setLoading(true)
    try {
      const basePayload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        photoUrl: formData.photoUrl,
        status: formData.status,
        seragam: formData.seragam, // 🟢 Masukkan Seragam ke Payload
        cities: formData.cities,
        services: formData.services,
        // 👈 TAMBAHAN: Simpan Kunci Area (null jika dikosongkan/otomatis)
        target_area_override: formData.target_area_override || null,
      }

      if (editMode && editId) {
        // --- UPDATE MODE ---
        await updateDoc(doc(db, 'cleaners', editId), {
            ...basePayload,
            rating: parseFloat(formData.rating),
            saldo: parseFloat(formData.saldo), 
            isBookingActive: formData.isBookingActive
        })
        alert("✅ Data Berhasil Diupdate")
      } else {
        // --- CREATE MODE ---
        const createCleanerFn = httpsCallable(functions, 'createCleaner')
        
        // 🟢 Kirim Referral & Seragam ke Backend
        const result = await createCleanerFn({
            ...basePayload,
            referralCodeInput: formData.referralCodeInput 
        })
        
        const response = result.data
        if (response.success) {
            alert(`✅ SUKSES!\n\nCleaner ID: ${response.id_number}\nPassword Sementara: ${response.tempPassword}\n\nHarap catat password ini dan berikan ke Cleaner.`)
        } else {
            throw new Error(response.error || "Gagal membuat cleaner")
        }
      }

      resetForm()
      fetchCleaners()

    } catch (err) {
      console.error(err)
      alert("❌ Gagal: " + err.message)
    }
    setLoading(false)
  }

  // 🔹 6. RESET PASSWORD
  const handleResetPassword = async (cleaner) => {
    const newPassword = window.prompt(`Masukkan password baru untuk ${cleaner.name}:`);
    if (!newPassword) return; 
    if (newPassword.length < 6) return alert("Password minimal 6 karakter!");

    const confirm = window.confirm(`Yakin ingin mengubah password untuk ${cleaner.name}?`);
    if (!confirm) return;

    setLoading(true);
    try {
      const setPassFn = httpsCallable(functions, 'setMitraPassword');
      const result = await setPassFn({
        uid: cleaner.id, 
        password: newPassword
      });

      if (result.data.success) {
        alert("✅ Password berhasil diubah!");
      } else {
        throw new Error(result.data.error || "Gagal mengubah password");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Gagal Reset Password: " + err.message);
    }
    setLoading(false);
  };

  // 🔹 7. UTILS
  const handleEdit = (c) => {
    setEditId(c.id)
    setEditMode(true)
    setShowForm(true)
    setPreviewUrl(c.photoUrl)
    
    setFormData({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      id_number: c.id_number || '',
      gender: c.gender || 'wanita',
      photoUrl: c.photoUrl || '',
      rating: c.rating || 5.0,
      saldo: c.saldo || 0,
      status: c.status,
      seragam: c.seragam || false, // 🟢 Populate saat Edit
      isBookingActive: c.isBookingActive !== undefined ? c.isBookingActive : true,
      cities: c.cities || [],
      services: c.services || [],
      referralCodeInput: '',
      target_area_override: c.target_area_override || '' // 👈 TAMBAHAN
    })
  }

  const handleDelete = async (id) => {
    if(!window.confirm("Hapus cleaner ini selamanya?")) return
    try {
      await deleteDoc(doc(db, 'cleaners', id))
      fetchCleaners()
    } catch(err) {
      alert("Gagal hapus: " + err.message)
    }
  }

  const toggleStatus = async (c) => {
    try {
      await updateDoc(doc(db, 'cleaners', c.id), { status: !c.status })
      fetchCleaners()
    } catch(err) {
      alert("Gagal update status.")
    }
  }

  const resetForm = () => {
    setShowForm(false)
    setEditMode(false)
    setEditId(null)
    setPreviewUrl(null)
    setFormData(initialFormState)
  }

  const openDetail = (c) => {
    setSelectedCleaner(c)
    setDetailOpen(true)
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 p-6 font-sans">
      
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">🧹 Manajemen Cleaner</h1>
          <p className="text-gray-400 text-sm">Kelola data mitra cleaner (Backend Connected).</p>
        </div>
        <button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
        >
          <span>➕</span> Tambah Cleaner
        </button>
      </div>

      {/* SEARCH BAR */}
      <div className="mb-6">
        <input 
          type="text" 
          placeholder="Cari..." 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full p-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:ring-2 focus:ring-blue-600 outline-none"
        />
      </div>

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-gray-900 w-full max-w-4xl rounded-2xl border border-gray-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            
            <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-800/50">
              <h2 className="text-lg font-bold text-white">
                {editMode ? '✏️ Edit Data Cleaner' : '➕ Registrasi Cleaner Baru'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-white text-xl">✕</button>
            </div>

            <form onSubmit={handleSave} className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* KOLOM KIRI */}
              <div className="space-y-4">
                <h3 className="text-blue-400 font-semibold border-b border-gray-800 pb-2">Informasi Pribadi</h3>
                
                <div className="flex gap-4">
                  <div className="w-24">
                    <label className="block text-xs text-gray-400 mb-1">Foto</label>
                    <div className="w-24 h-24 bg-gray-800 rounded-lg border border-gray-700 flex items-center justify-center overflow-hidden relative group">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gray-600 text-2xl">📷</span>
                      )}
                      <input type="file" accept="image/*" onChange={(e) => handleUploadPhoto(e.target.files[0])} 
                        className="absolute inset-0 opacity-0 cursor-pointer" />
                      {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs text-white">Loading...</div>}
                    </div>
                  </div>
                  <div className="flex-1 space-y-3">
                    <input type="text" placeholder="Nama Lengkap" required
                      value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                      className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white" />
                    
                    <input type="email" placeholder="Email" required disabled={editMode}
                      value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                      className={`w-full p-2 bg-gray-800 border border-gray-700 rounded text-white ${editMode ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="No Telepon" required
                    value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="p-2 bg-gray-800 border border-gray-700 rounded text-white" />
                  
                  <input type="text" placeholder={editMode ? "ID (Read Only)" : "Auto Generated"} 
                    value={formData.id_number} disabled
                    className="p-2 bg-gray-800 border border-gray-700 rounded text-gray-500 cursor-not-allowed" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})}
                    className="p-2 bg-gray-800 border border-gray-700 rounded text-white">
                    <option value="wanita">Wanita</option>
                    <option value="pria">Pria</option>
                  </select>

                  {!editMode && (
                    <input type="text" placeholder="Kode Referral (Opsional)" 
                      value={formData.referralCodeInput} onChange={e => setFormData({...formData, referralCodeInput: e.target.value})}
                      className="p-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500" />
                  )}
                </div>

                {/* CITIES */}
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Area Kerja</label>
                  <div className="flex gap-2 mb-2">
                    <input type="text" placeholder="Contoh: jakarta" 
                      value={cityInput} onChange={e => setCityInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCity(e)}
                      className="flex-1 p-2 bg-gray-800 border border-gray-700 rounded text-white" />
                    <button type="button" onClick={addCity} className="bg-gray-700 px-3 rounded text-white">+</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.cities.map((city, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-900/30 text-blue-300 rounded text-xs border border-blue-900 flex items-center gap-1">
                        {city} <button type="button" onClick={() => removeCity(city)} className="hover:text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* 👈 TAMBAHAN: Dropdown Kunci Area Target */}
                <div className="mt-4 p-3 bg-gray-800/80 rounded-lg border border-gray-700">
                  <label className="text-xs font-semibold text-gray-300 block mb-2">🔒 Area Target Performa (Opsional)</label>
                  <select
                    value={formData.target_area_override}
                    onChange={(e) => setFormData({ ...formData, target_area_override: e.target.value })}
                    className="w-full p-2 bg-gray-900 border border-gray-700 rounded text-white text-sm outline-none focus:border-blue-500 uppercase"
                  >
                    <option value="">[ OTOMATIS DARI CITIES ]</option>
                    <option value="jabodetabek">JABODETABEK</option>
                    <option value="bandung">BANDUNG</option>
                    <option value="semarang">SEMARANG</option>
                    <option value="yogyakarta">YOGYAKARTA</option>
                    <option value="surabaya">SURABAYA</option>
                    <option value="denpasar">DENPASAR</option>
                    <option value="makassar">MAKASSAR</option>
                    <option value="manado">MANADO</option>
                    <option value="balikpapan">BALIKPAPAN</option>
                    <option value="batam">BATAM</option>
                    <option value="palembang">PALEMBANG</option>
                    <option value="pekanbaru">PEKANBARU</option>
                    <option value="medan">MEDAN</option>
                  </select>
                  <p className="text-[10px] text-gray-500 mt-1">
                    *Biarkan <b>[Otomatis]</b> agar target menyesuaikan lokasi kota di atas.
                  </p>
                </div>

              </div>

              {/* KOLOM KANAN */}
              <div className="space-y-4">
                <h3 className="text-blue-400 font-semibold border-b border-gray-800 pb-2">Layanan & Status</h3>

                <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                  <p className="text-xs text-gray-400 mb-2">Checklist layanan:</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                    {SERVICE_MASTER.map(srv => {
                      const isChecked = formData.services.some(s => s.serviceId === srv.code)
                      return (
                        <label key={srv.code} className={`flex items-center justify-between p-2 rounded cursor-pointer border ${isChecked ? 'bg-blue-900/20 border-blue-600' : 'border-gray-700 hover:bg-gray-800'}`}>
                          <div className="flex items-center gap-3">
                            <input type="checkbox" className="w-4 h-4"
                              checked={isChecked}
                              onChange={(e) => toggleService(srv.code, e.target.checked)} />
                            <span className="text-sm font-medium">{srv.label}</span>
                          </div>
                          <span className="text-xs font-mono text-gray-500">{srv.code}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Rating</label>
                    <input type="number" step="0.1" disabled={!editMode}
                      value={formData.rating} onChange={e => setFormData({...formData, rating: e.target.value})}
                      className={`w-full p-2 bg-gray-800 border border-gray-700 rounded text-white ${!editMode ? 'opacity-50' : ''}`} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Saldo (Rp)</label>
                    <input type="number" disabled={!editMode}
                      value={formData.saldo} onChange={e => setFormData({...formData, saldo: e.target.value})}
                      className={`w-full p-2 bg-gray-800 border border-gray-700 rounded text-white ${!editMode ? 'opacity-50' : ''}`} />
                  </div>
                </div>

                {/* 🟢 TOGGLE SERAGAM (BARU) */}
                <div className="flex items-center justify-between bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm">Punya Seragam?</span>
                  <input type="checkbox" checked={formData.seragam}
                    onChange={e => setFormData({...formData, seragam: e.target.checked})}
                    className="w-5 h-5 toggle-checkbox" />
                </div>

                <div className="flex items-center justify-between bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm">Status Akun Aktif</span>
                  <input type="checkbox" checked={formData.status}
                    onChange={e => setFormData({...formData, status: e.target.checked})}
                    className="w-5 h-5 toggle-checkbox" />
                </div>
                
                 {editMode && (
                    <div className="flex items-center justify-between bg-gray-800 p-3 rounded-lg">
                      <span className="text-sm">Bisa Terima Order?</span>
                      <input type="checkbox" checked={formData.isBookingActive}
                        onChange={e => setFormData({...formData, isBookingActive: e.target.checked})}
                        className="w-5 h-5 toggle-checkbox" />
                    </div>
                 )}
              </div>

              <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-800 flex justify-end gap-3">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-gray-400 hover:text-white">Batal</button>
                <button type="submit" disabled={loading} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold">
                  {loading ? 'Memproses...' : (editMode ? 'Simpan Perubahan' : 'Buat Cleaner Baru')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LIST DATA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? <p className="text-gray-400 col-span-full text-center">Memuat Data...</p> : 
         filtered.length === 0 ? <p className="text-gray-500 col-span-full text-center py-10">Data tidak ditemukan.</p> :
         filtered.map(c => (
           <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-600 transition group">
             <div className="p-4 flex items-start gap-3 relative">
               <img src={c.photoUrl || 'https://via.placeholder.com/150'} alt={c.name} 
                 className="w-14 h-14 rounded-full object-cover border-2 border-gray-700" />
               <div className="flex-1 min-w-0">
                 <h3 className="font-bold text-white truncate">{c.name}</h3>
                 <p className="text-xs text-gray-400 truncate">{c.email}</p>
                 <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-[10px] bg-blue-900/50 text-blue-200 px-1.5 rounded">{c.id_number}</span>
                   <span className={`text-xs px-1.5 py-0.5 rounded ${c.status ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                     {c.status ? 'Aktif' : 'Nonaktif'}
                   </span>

                    {/* 👈 TAMBAHAN: Indikator Buka Aplikasi */}
                    {c.is_app_open ? (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-blue-900/40 text-blue-300 flex items-center gap-1.5 border border-blue-800">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></div>
                        Sedang Buka App
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-gray-800 text-gray-400 flex items-center gap-1.5 border border-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-500"></div>
                        Tutup App {c.last_app_opened ? `(${formatLastSeen(c.last_app_opened)})` : ''}
                      </span>
                    )}

                 </div>
               </div>
               <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition">
                 <button onClick={() => openDetail(c)} className="bg-gray-700 hover:bg-gray-600 p-1.5 rounded text-xs text-white">Info</button>
               </div>
             </div>
             
             {/* 🟢 TOMBOL AKSI LENGKAP DI DEPAN */}
             <div className="p-3 bg-gray-800/50 border-t border-gray-800 flex justify-between gap-2">
               <button 
                 onClick={() => toggleStatus(c)} 
                 className={`flex-1 text-xs py-1.5 rounded font-medium ${c.status ? 'text-yellow-400 hover:bg-yellow-900/20' : 'text-green-400 hover:bg-green-900/20'}`}
                 title={c.status ? "Nonaktifkan" : "Aktifkan"}
               >
                 {c.status ? '🛑' : '✅'}
               </button>

               <button 
                 onClick={() => handleEdit(c)} 
                 className="flex-1 text-xs py-1.5 rounded font-medium text-blue-400 hover:bg-blue-900/20"
                 title="Edit Data"
               >
                 ✏️
               </button>

               <button 
                 onClick={() => handleResetPassword(c)} 
                 className="flex-1 text-xs py-1.5 rounded font-medium text-orange-400 hover:bg-orange-900/20"
                 title="Reset Password"
               >
                 🔑
               </button>

               <button 
                 onClick={() => handleDelete(c.id)} 
                 className="flex-1 text-xs py-1.5 rounded font-medium text-red-400 hover:bg-red-900/20"
                 title="Hapus Permanen"
               >
                 🗑️
               </button>
             </div>
           </div>
         ))
        }
      </div>

      {/* DETAIL SIDEBAR */}
      {detailOpen && selectedCleaner && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDetailOpen(false)}></div>
          <div className="relative w-full max-w-md bg-gray-900 h-full shadow-2xl border-l border-gray-800 p-6 overflow-y-auto">
            <button onClick={() => setDetailOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">✕</button>
            
            <div className="flex flex-col items-center mb-6">
              <img src={selectedCleaner.photoUrl || 'https://via.placeholder.com/150'} alt={selectedCleaner.name} 
                className="w-24 h-24 rounded-full border-4 border-gray-800 shadow-lg object-cover mb-3" />
              <h2 className="text-xl font-bold text-white">{selectedCleaner.name}</h2>
              <p className="text-gray-400 text-sm">ID: {selectedCleaner.id_number}</p>
              
              <p className="text-blue-400 text-xs font-mono mt-1 px-2 py-0.5 bg-blue-900/20 rounded border border-blue-900">
                Kode Ref: {selectedCleaner.referralCode || '-'}
              </p>
              
              <div className="mt-2 flex gap-2">
                <span className="px-2 py-1 rounded bg-gray-800 text-xs border border-gray-700">{selectedCleaner.gender}</span>
                <span className="px-2 py-1 rounded bg-gray-800 text-xs border border-gray-700">{selectedCleaner.phone}</span>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Area Kerja</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedCleaner.cities.length > 0 ? selectedCleaner.cities.map(city => (
                    <span key={city} className="px-2 py-1 bg-green-900/20 text-green-400 border border-green-900 rounded text-xs">
                      {city}
                    </span>
                  )) : <p className="text-gray-600 text-sm italic">Belum ada area kerja</p>}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Layanan Terdaftar</h4>
                <div className="space-y-2">
                  {selectedCleaner.services.length > 0 ? selectedCleaner.services.map((s, idx) => {
                    const label = SERVICE_MASTER.find(m => m.code === s.serviceId)?.label || s.serviceId
                    return (
                      <div key={idx} className="flex justify-between items-center bg-gray-800 p-2 rounded text-sm">
                        <span>{label}</span>
                        <span className="text-xs font-mono text-gray-500">{s.serviceId}</span>
                      </div>
                    )
                  }) : <p className="text-gray-600 text-sm italic">Belum ada layanan</p>}
                </div>
              </div>

              {/* 🟢 INFO SERAGAM DI DETAIL */}
              <div>
                 <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Data Mitra</h4>
                 <ul className="text-sm text-gray-400 space-y-2 bg-gray-800/50 p-3 rounded">
                    <li className="flex justify-between">
                        <span>Seragam:</span> 
                        <span className={selectedCleaner.seragam ? "text-green-400 font-bold" : "text-red-400"}>
                            {selectedCleaner.seragam ? "Punya" : "Tidak Punya"}
                        </span>
                    </li>
                    <li className="flex justify-between"><span>Diajak Oleh:</span> <span className="text-white">{selectedCleaner.referredBy || '-'}</span></li>
                    <li className="flex justify-between"><span>Total Mengajak:</span> <span className="text-white">{selectedCleaner.totalReferrals || 0} orang</span></li>
                 </ul>
              </div>
              
               <div className="pt-4 border-t border-gray-800 space-y-3">
                <button 
                  onClick={() => { setDetailOpen(false); handleEdit(selectedCleaner); }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded mb-2 font-medium"
                >
                  Edit Data Lengkap
                </button>

                <button 
                  onClick={() => handleResetPassword(selectedCleaner)}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2 rounded font-medium"
                >
                  🔑 Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}